import { Link } from "react-router-dom";

import GetIcon from "Components/Global/Icons";

import * as S from "./styles";

/**
 * Nome da entidade como âncora real na célula da tabela do admin.
 *
 * O ponto da existência deste componente é o `href`: só um <a href> de verdade
 * habilita Ctrl/Cmd+clique, botão do meio e "abrir em nova aba" do menu de
 * contexto do navegador. Um <div onClick> não aparece nesse menu.
 *
 * @param to         destino; `null`/vazio → texto simples, sem âncora
 * @param label      texto do link
 * @param fallbackId id usado como `#<id>` quando o label é vazio
 * @param newTab     força target="_blank" (só a aba "Pré-seleção de Projetos")
 * @param title      tooltip. ⚠️ Use só quando o link NÃO tiver texto próprio: o
 *                   title vira o nome acessível do link e substitui o texto
 *                   visível para o leitor de tela. Por isso as listagens passam
 *                   title no ícone de edição, mas não no nome da entidade.
 */
export const TableRowLink = ({
  to,
  label,
  fallbackId,
  newTab = false,
  title,
}) => {
  const text = label || (fallbackId ? `#${fallbackId}` : "");

  if (!to) return <S.NamePlain>{text}</S.NamePlain>;

  if (newTab) {
    return (
      <S.NameAnchor
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
      >
        {text}
      </S.NameAnchor>
    );
  }

  return (
    <S.NameLink to={to} title={title}>
      {text}
    </S.NameLink>
  );
};

/**
 * Ícone de edição da última coluna ("Ações") como âncora, pelo mesmo motivo do
 * TableRowLink. Sem `to`, mantém o ícone visível porém inerte.
 */
export const TableActionLink = ({ to, title }) => (
  <S.ActionWrapper>
    {to ? (
      <Link to={to} title={title}>
        <GetIcon name="Edit" />
      </Link>
    ) : (
      <GetIcon name="Edit" />
    )}
  </S.ActionWrapper>
);

/**
 * Injeta o `Cell` de link na coluna de nome de um array de colunas já pronto,
 * sem tocar no `columns.js` e sem mutar o array recebido — os `downloadColumns`
 * de várias telas compartilham essas mesmas definições e o CSV não pode receber
 * JSX.
 *
 * O `accessor` é preservado de propósito: a ordenação e a busca do react-table
 * leem o valor do accessor, não o que o `Cell` renderiza.
 *
 * @param columns  array de colunas (importado de ./columns)
 * @param accessor accessor (string) da coluna de nome
 * @param to       (row) => string|null — destino da linha
 * @param rowId    (row) => id exibido como `#<id>` quando o nome é vazio. Nas
 *                 abas da seletiva a linha tem `id` (do vínculo) E `project_id`
 *                 (do projeto) — quem chama diz qual dos dois vale.
 * @param title    tooltip fixo do link
 * @param newTab   abre em nova aba
 */
export const withRowLink = (
  columns,
  { accessor, to, rowId = (row) => row.id, title, newTab },
) =>
  columns.map((column) =>
    column.accessor === accessor
      ? {
          ...column,
          Cell: ({ cell }) => (
            <TableRowLink
              to={to(cell.row.original)}
              label={cell.row.original[accessor]}
              fallbackId={rowId(cell.row.original)}
              title={title}
              newTab={newTab}
            />
          ),
        }
      : column,
  );

export default TableRowLink;
